import { createSlice } from "@reduxjs/toolkit";
import {
  availableBoxResponse,
  BoxConfig,
  BoxConfigResponse,
} from "../../typings/interfaces/responses/boxStorageResponse";

import {
  addBoxStorageAction,
  createBoxAction,
  getAvailableBoxAction,
  getBoxStorageAction,
  removeBoxStorageAction,
  renewBoxStorageAction,
  validateBoxStorageAction,
} from "../actions/boxStorageAction";

import {
  AddBoxStorageBody,
  BoxStorageResponse,
  BoxStorageValidationResponse,
} from "../../typings/interfaces/responses/boxStorageResponse";
import {
  BaseResponse,
  BaseResponseSingle,
} from "../../typings/interfaces/responses";

export interface BoxStorageState {
  addBoxStorage: BaseResponseSingle<BoxStorageResponse> | null;
  addBoxStorageLoading: boolean;
  addBoxStorageError: string | null;

  fetchedBoxStorage: BaseResponse<AddBoxStorageBody> | null;

  validateBoxStorage: BaseResponseSingle<BoxStorageValidationResponse> | null;
  validateBoxStorageLoading: boolean;
  validateBoxStorageError: string | null;

  fetchedBoxStorageLoading: boolean;
  fetchedBoxStorageError: string | null;

  removeBoxStorageLoading: boolean;
  removeBoxStorageError: string | null;

  renewBoxStorageLoading: boolean;
  renewBoxStorageError: string | null;

  boxesConfig: BaseResponse<BoxConfig> | null;
  boxesConfigLoading: boolean;
  boxesConfigError: string | null;

  availableboxes: BaseResponse<availableBoxResponse> | null;
  availableboxesLoading: boolean;
  availableboxesConfigError: string | null;

  createBox: BaseResponseSingle<BoxConfigResponse> | null;
  createBoxLoading: boolean;
  createBoxError: string | null;
}

const initialState: BoxStorageState = {
  addBoxStorage: null,
  addBoxStorageLoading: false,
  addBoxStorageError: null,

  validateBoxStorage: null,
  validateBoxStorageLoading: false,
  validateBoxStorageError: null,

  fetchedBoxStorage: null,
  fetchedBoxStorageLoading: false,
  fetchedBoxStorageError: null,

  removeBoxStorageLoading: false,
  removeBoxStorageError: null,

  renewBoxStorageLoading: false,
  renewBoxStorageError: null,

  boxesConfig: null,
  boxesConfigLoading: false,
  boxesConfigError: null,

  availableboxes: null,
  availableboxesLoading: false,
  availableboxesConfigError: null,

  createBox: null,
  createBoxLoading: false,
  createBoxError: null,
};

const boxStorageSlice = createSlice({
  name: "boxStorage",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(addBoxStorageAction.pending, (state) => {
        state.addBoxStorageLoading = true;
        state.addBoxStorageError = null;
      })
      .addCase(addBoxStorageAction.fulfilled, (state, action) => {
        state.addBoxStorageLoading = false;
        state.addBoxStorage = (action.payload as any).response;
      })
      .addCase(addBoxStorageAction.rejected, (state, action) => {
        state.addBoxStorageLoading = false;
        state.addBoxStorageError =
          (action.payload as any)?.message || "Failed to add box storage.";
      });

    builder
      .addCase(validateBoxStorageAction.pending, (state) => {
        state.validateBoxStorageLoading = true;
        state.validateBoxStorageError = null;
        state.validateBoxStorage = null;
      })
      .addCase(validateBoxStorageAction.fulfilled, (state, action) => {
        state.validateBoxStorageLoading = false;
        state.validateBoxStorage = (action.payload as any).response;
      })
      .addCase(validateBoxStorageAction.rejected, (state, action) => {
        state.validateBoxStorageLoading = false;
        state.validateBoxStorageError =
          (action.payload as any)?.message || "Box validation failed.";
      });

    builder
      .addCase(getBoxStorageAction.pending, (state) => {
        state.fetchedBoxStorageLoading = true;
        state.fetchedBoxStorageError = null;
        state.fetchedBoxStorage = null;
      })
      .addCase(getBoxStorageAction.fulfilled, (state, action) => {
        state.fetchedBoxStorageLoading = false;
        state.fetchedBoxStorage =
          action.payload as BaseResponse<AddBoxStorageBody>;
      })
      .addCase(getBoxStorageAction.rejected, (state, action) => {
        state.fetchedBoxStorageLoading = false;
        state.fetchedBoxStorageError =
          (action.payload as any)?.message || "Failed to fetch box storage.";
      });

    builder
      .addCase(removeBoxStorageAction.pending, (state) => {
        state.removeBoxStorageLoading = true;
        state.removeBoxStorageError = null;
      })
      .addCase(removeBoxStorageAction.fulfilled, (state) => {
        state.removeBoxStorageLoading = false;
        state.fetchedBoxStorage = null;
      })
      .addCase(removeBoxStorageAction.rejected, (state, action) => {
        state.removeBoxStorageLoading = false;
        state.removeBoxStorageError =
          (action.payload as any)?.message || "Failed to remove box storage.";
      });

    builder
      .addCase(renewBoxStorageAction.pending, (state) => {
        state.renewBoxStorageLoading = true;
        state.renewBoxStorageError = null;
      })
      .addCase(renewBoxStorageAction.fulfilled, (state) => {
        state.renewBoxStorageLoading = false;
      })
      .addCase(renewBoxStorageAction.rejected, (state, action) => {
        state.renewBoxStorageLoading = false;
        state.renewBoxStorageError =
          (action.payload as any)?.message || "Failed to renew box storage.";
      });

    builder
      .addCase(getAvailableBoxAction.pending, (state) => {
        state.boxesConfigLoading = true;
        state.boxesConfigError = null;
        state.boxesConfig = null;
      })
      .addCase(getAvailableBoxAction.fulfilled, (state, action) => {
        state.boxesConfigLoading = false;
        state.boxesConfig =
          action.payload as BaseResponse<availableBoxResponse>;
      })
      .addCase(getAvailableBoxAction.rejected, (state, action) => {
        state.boxesConfigLoading = false;
        state.boxesConfigError =
          (action.payload as any)?.message || "Failed to fetch box configs.";
      })
      .addCase(createBoxAction.pending, (state) => {
        state.createBoxLoading = true;
        state.createBoxError = null;
      })
      .addCase(createBoxAction.fulfilled, (state, action) => {
        state.createBoxLoading = false;
        state.createBox = action.payload;
      })
      .addCase(createBoxAction.rejected, (state, action) => {
        state.createBoxLoading = false;
        state.createBoxError =
          (action.payload as any)?.message || "Failed to create new box.";
      });
  },
});

export default boxStorageSlice.reducer;
export const boxStorageSliceActions = boxStorageSlice.actions;
